import { useRef, useMemo } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import type { HeadwearType, OutfitType, AccessoryType, ColorTintType } from "../../store/dressup";

export type AnimalType = "bunny" | "frog" | "tanuki" | "kitsune" | "monkey";

type Props = {
  animalType?: AnimalType;
  waving?: boolean;
  jumping?: boolean;
  walking?: boolean;
  walkSpeed?: number;
  headwear?: HeadwearType;
  outfit?: OutfitType;
  accessory?: AccessoryType;
  colorTint?: ColorTintType;
  onJumpComplete?: () => void;
  /** 1フレームの経過時間の上限（秒）。描き直しを間引く Canvas 用。省略時は上限なし */
  maxDelta?: number;
};

const CHARACTER_TEXTURES: Record<AnimalType, string> = {
  bunny: "/assets/characters/bunny.png",
  frog: "/assets/characters/frog.png",
  tanuki: "/assets/characters/tanuki.png",
  kitsune: "/assets/characters/kitsune.png",
  monkey: "/assets/characters/monkey.png",
};

// 頭部の高さオフセット（着せ替え帽子の基準位置）
const HEAD_OFFSETS: Record<AnimalType, [number, number, number]> = {
  bunny: [0, 0.62, 0],
  frog: [0, 0.48, 0],
  tanuki: [0, 0.54, 0],
  kitsune: [0, 0.58, 0],
  monkey: [0, 0.55, 0],
};

// 目の位置オフセット（サングラスなどの基準位置）
const EYE_OFFSETS: Record<AnimalType, [number, number, number]> = {
  bunny: [0, 0.26, 0.04],
  frog: [0, 0.22, 0.04],
  tanuki: [0, 0.20, 0.04],
  kitsune: [0, 0.22, 0.04],
  monkey: [0, 0.20, 0.04],
};

export function Animal3DModel({
  animalType = "bunny",
  waving = false,
  jumping = false,
  walking = false,
  walkSpeed = 10,
  headwear = "none",
  outfit = "default",
  accessory = "none",
  colorTint = "default",
  onJumpComplete,
  maxDelta = Infinity,
}: Props) {
  const rootGroup = useRef<THREE.Group>(null);
  const standeeGroup = useRef<THREE.Group>(null);
  const jumpProgress = useRef(0);

  // キャラクターテクスチャの読み込み
  const textureUrl = CHARACTER_TEXTURES[animalType] || CHARACTER_TEXTURES.bunny;
  const texture = useLoader(THREE.TextureLoader, textureUrl);

  useMemo(() => {
    if (texture) {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
    }
  }, [texture]);

  // カラーティントフィルター
  const tintColor = useMemo(() => {
    if (colorTint === "sakura") return new THREE.Color("#ffd6e0");
    if (colorTint === "mint") return new THREE.Color("#c1e8de");
    if (colorTint === "latte") return new THREE.Color("#d7bfa8");
    if (colorTint === "snow") return new THREE.Color("#f0fdf4");
    return new THREE.Color("#ffffff");
  }, [colorTint]);

  useFrame((_, rawDelta) => {
    if (!rootGroup.current || !standeeGroup.current) return;
    // 描き直しを間引く Canvas（frameloop="demand"）では、久しぶりの1フレームの delta が大きくなりジャンプが一瞬で終わるため、maxDelta で上限を置く
    const delta = Math.min(rawDelta, maxDelta);

    // 1. 歩行アニメーション (トコトコ弾むSquash & Stretchモーション)
    if (walking) {
      const time = Date.now() * 0.001 * walkSpeed;
      // 上下にピョコピョコ跳ねる
      const hop = Math.abs(Math.sin(time * 2)) * 0.14;
      // 左右にキュートにチルト（傾き）
      const tilt = Math.sin(time) * 0.09;
      // 着地時の弾み（Squash & Stretch）
      const squash = 1 - Math.sin(time * 2) * 0.06;
      const stretch = 1 + Math.sin(time * 2) * 0.06;

      rootGroup.current.position.y = hop;
      standeeGroup.current.rotation.z = tilt;
      standeeGroup.current.scale.set(stretch, squash, 1);
    } else {
      // 2. アイドル時の自然な呼吸
      const time = Date.now() * 0.002;
      const breathe = 1 + Math.sin(time * 2) * 0.02;
      standeeGroup.current.scale.set(1, breathe, 1);
      standeeGroup.current.rotation.z = 0;

      // 3. 手を振る・喜ぶ (Waving)
      if (waving) {
        standeeGroup.current.rotation.z = Math.sin(Date.now() * 0.008) * 0.12;
      }

      // 4. ジャンプ (Jumping)
      if (jumping) {
        jumpProgress.current += delta * 3.5;
        const progress = jumpProgress.current;
        if (progress <= 1) {
          const jumpH = Math.sin(progress * Math.PI) * 0.55;
          rootGroup.current.position.y = jumpH;
          // 空中でくるりと楽しそうにY軸回転
          standeeGroup.current.rotation.y = progress * Math.PI * 2;
          // 頂点での伸び
          standeeGroup.current.scale.set(0.92, 1.15, 1);
        } else {
          jumpProgress.current = 0;
          rootGroup.current.position.y = 0;
          standeeGroup.current.rotation.y = 0;
          standeeGroup.current.scale.set(1, 1, 1);
          onJumpComplete?.();
        }
      } else {
        rootGroup.current.position.y = Math.sin(time) * 0.02;
      }
    }
  });

  const headPos = HEAD_OFFSETS[animalType] || [0, 0.55, 0];
  const eyePos = EYE_OFFSETS[animalType] || [0, 0.22, 0.04];
  void outfit; // 添付の和服が初期状態で完成しているため保持

  return (
    <group ref={rootGroup} dispose={null}>
      {/* 立体スタンド本体 */}
      <group ref={standeeGroup}>
        {/* 前面テクスチャ（忠実な高精細イラスト） */}
        <mesh position={[0, 0.28, 0.01]} castShadow receiveShadow>
          <planeGeometry args={[1.35, 1.35]} />
          <meshStandardMaterial
            map={texture}
            transparent
            alphaTest={0.05}
            roughness={0.4}
            metalness={0.05}
            color={tintColor}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* 立体的な厚みレイヤー（微細な奥行きバックプレート） */}
        <mesh position={[0, 0.28, -0.01]}>
          <planeGeometry args={[1.35, 1.35]} />
          <meshStandardMaterial
            map={texture}
            transparent
            alphaTest={0.05}
            roughness={0.8}
            color="#e2d9c8"
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* やわらかな足元丸影 */}
        <mesh position={[0, -0.36, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.38, 32]} />
          <meshBasicMaterial color="#3e2723" transparent opacity={0.25} />
        </mesh>
      </group>

      {/* =================================================================== */}
      {/* 3D 着せ替えアクセサリー（帽子・メガネ・髪飾り）                      */}
      {/* =================================================================== */}
      {/* 1. 麦わら帽子 */}
      {headwear === "straw_hat" && (
        <group position={[headPos[0], headPos[1] + 0.12, headPos[2] + 0.02]} rotation={[-0.15, 0, 0.1]}>
          <mesh castShadow>
            <cylinderGeometry args={[0.42, 0.42, 0.025, 32]} />
            <meshStandardMaterial color="#e9c46a" roughness={0.7} />
          </mesh>
          <mesh position={[0, 0.07, 0]} castShadow>
            <cylinderGeometry args={[0.22, 0.24, 0.12, 32]} />
            <meshStandardMaterial color="#f4a261" roughness={0.8} />
          </mesh>
          {/* 赤いリボン */}
          <mesh position={[0, 0.03, 0]}>
            <torusGeometry args={[0.23, 0.015, 8, 32]} />
            <meshStandardMaterial color="#e63946" />
          </mesh>
        </group>
      )}

      {/* 2. シルクハット */}
      {headwear === "top_hat" && (
        <group position={[headPos[0], headPos[1] + 0.16, headPos[2]]} rotation={[-0.1, 0, -0.08]}>
          <mesh castShadow>
            <cylinderGeometry args={[0.32, 0.32, 0.02, 32]} />
            <meshStandardMaterial color="#1f2421" />
          </mesh>
          <mesh position={[0, 0.14, 0]} castShadow>
            <cylinderGeometry args={[0.18, 0.2, 0.26, 32]} />
            <meshStandardMaterial color="#2b2d42" />
          </mesh>
          <mesh position={[0, 0.03, 0]}>
            <torusGeometry args={[0.19, 0.012, 8, 32]} />
            <meshStandardMaterial color="#e63946" />
          </mesh>
        </group>
      )}

      {/* 3. ベレー帽 */}
      {headwear === "beret" && (
        <group position={[headPos[0], headPos[1] + 0.06, headPos[2]]} rotation={[-0.2, 0, 0.35]}>
          <mesh castShadow>
            <sphereGeometry args={[0.26, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2.2]} />
            <meshStandardMaterial color="#9d0208" roughness={0.9} />
          </mesh>
          <mesh position={[0, 0.14, 0]}>
            <cylinderGeometry args={[0.01, 0.01, 0.04, 8]} />
            <meshStandardMaterial color="#9d0208" />
          </mesh>
        </group>
      )}

      {/* 4. 王冠 */}
      {headwear === "crown" && (
        <group position={[headPos[0], headPos[1] + 0.14, headPos[2]]} rotation={[-0.1, 0, 0]}>
          <mesh castShadow>
            <cylinderGeometry args={[0.18, 0.14, 0.12, 5]} />
            <meshStandardMaterial color="#ffd700" metalness={0.8} roughness={0.2} />
          </mesh>
          <mesh position={[0, 0.08, 0]}>
            <sphereGeometry args={[0.04, 12, 12]} />
            <meshStandardMaterial color="#e63946" metalness={0.5} roughness={0.2} />
          </mesh>
        </group>
      )}

      {/* 5. お花の髪飾り */}
      {headwear === "flower" && (
        <group position={[headPos[0] + 0.24, headPos[1] - 0.02, headPos[2] + 0.05]}>
          <mesh castShadow>
            <sphereGeometry args={[0.06, 16, 16]} />
            <meshStandardMaterial color="#ffb4a2" />
          </mesh>
          {[0, 1, 2, 3, 4].map((i) => (
            <mesh
              key={i}
              position={[Math.cos((i * Math.PI * 2) / 5) * 0.07, Math.sin((i * Math.PI * 2) / 5) * 0.07, 0]}
            >
              <sphereGeometry args={[0.035, 12, 12]} />
              <meshStandardMaterial color="#ffccd5" />
            </mesh>
          ))}
        </group>
      )}

      {/* 6. 丸メガネ */}
      {accessory === "glasses" && (
        <group position={[eyePos[0], eyePos[1], eyePos[2] + 0.03]}>
          <mesh position={[-0.16, 0, 0]}>
            <torusGeometry args={[0.12, 0.015, 12, 24]} />
            <meshStandardMaterial color="#3e2723" />
          </mesh>
          <mesh position={[0.16, 0, 0]}>
            <torusGeometry args={[0.12, 0.015, 12, 24]} />
            <meshStandardMaterial color="#3e2723" />
          </mesh>
          <mesh position={[0, 0.01, 0]}>
            <boxGeometry args={[0.09, 0.015, 0.01]} />
            <meshStandardMaterial color="#3e2723" />
          </mesh>
        </group>
      )}

      {/* 7. サングラス */}
      {accessory === "sunglasses" && (
        <group position={[eyePos[0], eyePos[1], eyePos[2] + 0.03]}>
          <mesh position={[-0.15, 0, 0]}>
            <boxGeometry args={[0.18, 0.09, 0.015]} />
            <meshStandardMaterial color="#111827" roughness={0.1} />
          </mesh>
          <mesh position={[0.15, 0, 0]}>
            <boxGeometry args={[0.18, 0.09, 0.015]} />
            <meshStandardMaterial color="#111827" roughness={0.1} />
          </mesh>
          <mesh position={[0, 0.015, 0]}>
            <boxGeometry args={[0.14, 0.015, 0.01]} />
            <meshStandardMaterial color="#111827" />
          </mesh>
        </group>
      )}

      {/* 8. レトロカメラ */}
      {accessory === "camera" && (
        <group position={[0, 0.02, 0.08]} rotation={[0.15, 0, 0]}>
          <mesh castShadow>
            <boxGeometry args={[0.2, 0.13, 0.07]} />
            <meshStandardMaterial color="#4a4e69" roughness={0.4} />
          </mesh>
          <mesh position={[0, 0, 0.04]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.045, 0.045, 0.04, 16]} />
            <meshStandardMaterial color="#22223b" metalness={0.5} />
          </mesh>
        </group>
      )}
    </group>
  );
}
